import React, { useEffect } from 'react';
import DatePicker from '../../components/datePicker';
import { LomavisH2 } from '../../lomavisSharedUi/atoms/typography/LomavisH2';
import IsLoadingIndicator from '../../components/isLoadingIndicator';
import { LomavisGrowthCardSectionTemplate } from '../../lomavisSharedUi/templates/cards/LomavisGrowthCardSectionTemplate';
import { LomavisDistributionGrowthCard1 } from '../../lomavisSharedUi/organisms/cards/LomavisDistributionGrowthCard1';
import { useTranslate } from '@tolgee/react';
import { useSelector, useDispatch } from 'react-redux';
import { formatDate, getAllDates } from '../../utils/dateUtils';
import {
  getBarChartData,
  getDoughnutChartData,
  getGrowthMetricsEntries,
  getTableChartData
} from '../../utils/analytics/growthMetricsUtils';
import DemographicsMap from '@/components/analytics/demographicsMap';
import { getLocationsAUserHasAccessTo } from '../../redux/slices/location/locationsList';
import { getCurrentLocationUUID } from '../../utils/communicationUtils';

interface AllAnalyticsOverviewTabProps {
  dates: any;
  setDates: (dates: any) => void;
  plotsData: any;
  oldPlotsData: any;
  loading: boolean;
  error: boolean;
  followersGrowthConfig: any;
  allPlatformsGrowthConfig: any;
  dailyGrowthConfig: any;
  contentGrowthConfig: any;
  distrubutedGrowthConfig: any;
}

const AllAnalyticsOverviewTab: React.FC<AllAnalyticsOverviewTabProps> = ({
  dates,
  setDates,
  plotsData,
  oldPlotsData,
  loading,
  error,
  followersGrowthConfig,
  allPlatformsGrowthConfig,
  dailyGrowthConfig,
  contentGrowthConfig,
  distrubutedGrowthConfig
}) => {
  const { t } = useTranslate();
  const locationUuid = getCurrentLocationUUID();

  return (
    <div>
      <div className="d-flex flex-column flex-lg-row justify-content-lg-end">
        <DatePicker setDates={setDates} dates={dates} />
      </div>
      <div className="m-5" />
      <LomavisH2 title={t('platform_analytics.title.KPISection')} />

      {plotsData?.plots &&
        Object.keys(plotsData?.plots).length > 0 &&
        !loading && (
          <>
            <div className={'mb-5'} />

            <div className="mb-10">
              {plotsData?.plots && (
                <LomavisGrowthCardSectionTemplate
                  title={t('platform_analytics.followers')}
                  entries={getGrowthMetricsEntries(
                    plotsData?.plots || {},
                    oldPlotsData?.plots || {},
                    followersGrowthConfig,
                    t,
                    true
                  )}
                />
              )}
            </div>

            <div className="mb-10">
              {plotsData?.plots && (
                <LomavisGrowthCardSectionTemplate
                  title={t('platform_analytics.growth_overview')}
                  entries={getGrowthMetricsEntries(
                    plotsData?.plots || {},
                    oldPlotsData?.plots || {},
                    allPlatformsGrowthConfig,
                    t,
                    true
                  )}
                />
              )}
            </div>

            <div className={'mb-10'}>
              {plotsData?.plots && (
                <LomavisGrowthCardSectionTemplate
                  title={t('platform_analytics.growth_daily_overview')}
                  entries={getGrowthMetricsEntries(
                    plotsData?.plots || {},
                    oldPlotsData?.plots || {},
                    dailyGrowthConfig,
                    t,
                    true
                  )}
                />
              )}
            </div>

            <div className={'mb-10'}>
              {plotsData?.plots && (
                <LomavisGrowthCardSectionTemplate
                  title={t('platform_analytics.content_growth_overview')}
                  entries={getGrowthMetricsEntries(
                    plotsData?.plots || {},
                    oldPlotsData?.plots || {},
                    contentGrowthConfig,
                    t,
                    true
                  )}
                />
              )}
            </div>
            <div className={'mb-5'} />
          </>
        )}
      {!plotsData?.plots && !loading && !error && (
        <div
          className="d-flex justify-content-center align-items-center m-4"
          style={{ height: '200px' }}
        >
          <p className="text-muted">
            {t('platform_analytics.no_data_available')}
          </p>
        </div>
      )}
      {error && (
        <div
          className="d-flex justify-content-center align-items-center m-4"
          style={{ height: '200px' }}
        >
          <LomavisH2 title={t('platform_analytics.error_loading_data')} />
        </div>
      )}
      {loading && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '200px' }}
        >
          <IsLoadingIndicator />
        </div>
      )}
      {!loading && (
        <>
          <div className="m-5" />

          {locationUuid && <DemographicsMap locationUuid={locationUuid} />}
        </>
      )}
      <LomavisH2
        title={t('platform_analytics.title.comparingAndDetailsSectionTitle')}
      />
      <div className={'mb-5'} />
      {plotsData?.plots && oldPlotsData?.plots && (
        <LomavisDistributionGrowthCard1
          subTitle={`${formatDate(dates.startDate)} - ${formatDate(
            dates.endDate
          )} | ${formatDate(
            new Date(dates.startDate).setDate(
              new Date(dates.startDate).getDate() -
                getAllDates(dates.startDate, dates.endDate).length
            )
          )} - ${formatDate(
            new Date(dates.startDate).setDate(
              new Date(dates.startDate).getDate() - 1
            )
          )}`}
          title={t(
            'platform_analytics.title.distributed_growth_profile_followers'
          )}
          doughnutChartData={getDoughnutChartData(
            plotsData?.plots || {},
            distrubutedGrowthConfig.profile_followers,
            t
          )}
          tableData={getTableChartData(
            plotsData?.plots || {},
            oldPlotsData.plots || {},
            distrubutedGrowthConfig.profile_followers,
            t
          )}
          barChartData={getBarChartData(
            plotsData?.plots || {},
            distrubutedGrowthConfig.profile_followers,
            t,
            oldPlotsData.plots || {}
          )}
        />
      )}
      <div className="m-5" />
      {plotsData?.plots && oldPlotsData?.plots && (
        <LomavisDistributionGrowthCard1
          subTitle={`${formatDate(dates.startDate)} - ${formatDate(
            dates.endDate
          )} | ${formatDate(
            new Date(dates.startDate).setDate(
              new Date(dates.startDate).getDate() -
                getAllDates(dates.startDate, dates.endDate).length
            )
          )} - ${formatDate(
            new Date(dates.startDate).setDate(
              new Date(dates.startDate).getDate() - 1
            )
          )}`}
          title={t(
            'platform_analytics.title.distributed_growth_post_impressions'
          )}
          doughnutChartData={getDoughnutChartData(
            plotsData?.plots || {},
            distrubutedGrowthConfig.post_impressions,
            t
          )}
          tableData={getTableChartData(
            plotsData?.plots || {},
            oldPlotsData.plots || {},
            distrubutedGrowthConfig.post_impressions,
            t
          )}
          barChartData={getBarChartData(
            plotsData?.plots || {},
            distrubutedGrowthConfig.post_impressions,
            t,
            oldPlotsData.plots || {}
          )}
        />
      )}
      <div className="m-5" />
      {plotsData?.plots && oldPlotsData?.plots && (
        <LomavisDistributionGrowthCard1
          subTitle={`${formatDate(dates.startDate)} - ${formatDate(
            dates.endDate
          )} | ${formatDate(
            new Date(dates.startDate).setDate(
              new Date(dates.startDate).getDate() -
                getAllDates(dates.startDate, dates.endDate).length
            )
          )} - ${formatDate(
            new Date(dates.startDate).setDate(
              new Date(dates.startDate).getDate() - 1
            )
          )}`}
          title={t('platform_analytics.title.distributed_growth_post_reach')}
          doughnutChartData={getDoughnutChartData(
            plotsData?.plots || {},
            distrubutedGrowthConfig.post_reach,
            t
          )}
          tableData={getTableChartData(
            plotsData?.plots || {},
            oldPlotsData.plots || {},
            distrubutedGrowthConfig.post_reach,
            t
          )}
          barChartData={getBarChartData(
            plotsData?.plots || {},
            distrubutedGrowthConfig.post_reach,
            t,
            oldPlotsData.plots || {}
          )}
        />
      )}
      <div className="m-5" />
      {plotsData?.plots && oldPlotsData?.plots && (
        <LomavisDistributionGrowthCard1
          subTitle={`${formatDate(dates.startDate)} - ${formatDate(
            dates.endDate
          )} | ${formatDate(
            new Date(dates.startDate).setDate(
              new Date(dates.startDate).getDate() -
                getAllDates(dates.startDate, dates.endDate).length
            )
          )} - ${formatDate(
            new Date(dates.startDate).setDate(
              new Date(dates.startDate).getDate() - 1
            )
          )}`}
          title={t(
            'platform_analytics.title.distributed_growth_post_reactions'
          )}
          doughnutChartData={getDoughnutChartData(
            plotsData?.plots || {},
            distrubutedGrowthConfig.post_reactions,
            t
          )}
          tableData={getTableChartData(
            plotsData?.plots || {},
            oldPlotsData.plots || {},
            distrubutedGrowthConfig.post_reactions,
            t
          )}
          barChartData={getBarChartData(
            plotsData?.plots || {},
            distrubutedGrowthConfig.post_reactions,
            t,
            oldPlotsData.plots || {}
          )}
        />
      )}
      <div className="m-5" />
    </div>
  );
};

export default AllAnalyticsOverviewTab;
